import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Gem,
  Check,
  RotateCcw,
} from "lucide-react";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playSuccess } from "@/lib/studySfx";
import { useVictoryMusic } from "@/hooks/useVictoryMusic";
import { GameResultsScreen } from "@/components/study/GameResultsScreen";

const CRYSTALS_PER_KNOW = 5;
const EXIT_ANIMATION_MS = 320;

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

interface SwipeArenaModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

function FloatingCrystals({
  amount,
  onComplete,
}: {
  amount: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-2xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-6 h-6" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

export default function SwipeArenaMode({
  cards,
  lang: deckLang,
  onBack,
  onProgressComplete,
}: SwipeArenaModeProps) {
  const { t } = useTranslation();
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    setVoiceEnabled,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const [activeCards, setActiveCards] = useState<Card[]>([]);
  const [sessionCrystals, setSessionCrystals] = useState(0);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [isGameFinished, setIsGameFinished] = useState(false);
  /** Id of the current top card that has been revealed (flipped). */
  const [revealedCardId, setRevealedCardId] = useState<string | null>(null);
  /** Exit animation: 'right' = Know, 'down' = Forgot (re-queue). */
  const [exitDirection, setExitDirection] = useState<"right" | "down" | null>(
    null
  );

  const isGameFinishedRef = useRef(false);
  useEffect(() => {
    isGameFinishedRef.current = isGameFinished;
  }, [isGameFinished]);

  useVictoryMusic({
    isGameFinished,
    isSoundEnabled,
    gameVolume,
    endResult: "win",
  });


  useEffect(() => {
    if (filteredCards.length === 0) return;
    setActiveCards(shuffleArray(filteredCards));
    setRevealedCardId(null);
    setExitDirection(null);
  }, [filteredCards]);

  const topCard = activeCards[0];
  const topCardId = topCard ? topCard.id ?? topCard.front : null;
  const isRevealed = topCardId != null && revealedCardId === topCardId;

  const handleReveal = useCallback(
    (card: Card) => {
      const id = card.id ?? card.front;
      setRevealedCardId(id);
      const backText = (card.back || "").trim();
      if (backText && isVoiceEnabled) {
        void playAudio({
          card,
          side: "back",
          deckLang: deckLang ?? "en",
          volume: voiceVolume,
        });
      }
    },
    [isVoiceEnabled, voiceVolume, deckLang]
  );

  const resolveAndNext = useCallback(
    (know: boolean) => {
      if (activeCards.length === 0 || !topCard) return;
      setExitDirection(know ? "right" : "down");
      setTimeout(() => {
        const [first, ...rest] = activeCards;
        if (know) {
          if (isSoundEnabled) playSuccess(gameVolume * 0.7);
          const result = addCrystals(
            CRYSTALS_PER_KNOW,
            t("swipeArena.rewardReason"),
            { isLearningAction: true, silent: true }
          );
          const totalAdd =
            typeof result === "object" ? result.totalAdd : CRYSTALS_PER_KNOW;
          setFloatingCrystals(totalAdd);
          setSessionCrystals((s) => s + totalAdd);
          finishStudySession(1, false);
          setActiveCards(rest);
        } else {
          setActiveCards([...rest, first]);
        }
        setRevealedCardId(null);
        setExitDirection(null);
        const total = filteredCards.length;
        if (total > 0 && onProgressComplete) {
          const cardsCompleted = total - rest.length;
          const pct = Math.min(100, Math.round((cardsCompleted / total) * 100));
          onProgressComplete(pct);
        }
        if (rest.length === 0) {
          setIsGameFinished(true);
        }
      }, EXIT_ANIMATION_MS);
    },
    [
      activeCards,
      topCard,
      filteredCards.length,
      isSoundEnabled,
      gameVolume,
      addCrystals,
      finishStudySession,
      t,
      onProgressComplete,
    ]
  );

  const handleKnow = useCallback(() => {
    if (isRevealed && !exitDirection) resolveAndNext(true);
  }, [isRevealed, exitDirection, resolveAndNext]);

  const handleForgot = useCallback(() => {
    if (isRevealed && !exitDirection) resolveAndNext(false);
  }, [isRevealed, exitDirection, resolveAndNext]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isGameFinishedRef.current || !topCard) return;
      if (e.key === " ") {
        e.preventDefault();
        if (!isRevealed) handleReveal(topCard);
        return;
      }
      if (!isRevealed) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleKnow();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleForgot();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [topCard, isRevealed, handleReveal, handleKnow, handleForgot]);

  const handlePlayAgain = useCallback(() => {
    setIsGameFinished(false);
    isGameFinishedRef.current = false;
    setSessionCrystals(0);
    setActiveCards(shuffleArray(filteredCards));
    setRevealedCardId(null);
    setExitDirection(null);
  }, [filteredCards]);

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isSoundEnabled ? t("common.muteMusic") : t("common.unmuteMusic")
          }
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isVoiceEnabled
              ? t("common.voiceModeOn")
              : t("common.voiceModeOff")
          }
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("swipeArena.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("swipeArena.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  if (isGameFinished) {
    return (
      <GameResultsScreen
        sessionCrystals={sessionCrystals}
        onPlayAgain={handlePlayAgain}
        onBackToDeck={onBack ?? (() => {})}
        headerRow={headerRow}
        variant="victory"
      />
    );
  }

  return (
    <div className="flex flex-col w-full max-w-lg mx-auto px-4 min-h-[calc(100vh-6rem)] pb-10">
      <AnimatePresence>
        {floatingCrystals != null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}
      </AnimatePresence>

      {headerRow}

      <p className="text-center text-sm text-[hsl(var(--muted-foreground))] mb-4">
        {t("swipeArena.instruction", "Tap the card to flip and hear the translation. Swipe right if you know it, left to review again.")}
      </p>

      <div className="flex-1 flex flex-col items-center justify-center min-h-[22rem] relative">
        <div
          className="relative w-[92%] max-w-md min-h-[20rem] flex items-center justify-center"
          style={{ perspective: "1200px" }}
        >
          <AnimatePresence mode="wait">
            {topCard && (
              <motion.div
                key={topCardId}
                className="absolute inset-0 rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl cursor-pointer"
                style={{
                  backgroundColor: "hsl(var(--card-bg))",
                  background:
                    "linear-gradient(145deg, rgba(30,27,75,0.95) 0%, rgba(15,10,45,0.98) 100%)",
                  boxShadow:
                    "0 0 40px rgba(168,85,247,0.25), 0 20px 50px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                  transformStyle: "preserve-3d",
                }}
                initial={{ opacity: 0, scale: 0.95, y: 24 }}
                animate={
                  exitDirection === "right"
                    ? { x: 400, opacity: 0 }
                    : exitDirection === "down"
                      ? { y: 280, opacity: 0, scale: 0.9 }
                      : { opacity: 1, scale: 1, y: 0, x: 0 }
                }
                transition={
                  exitDirection
                    ? {
                        duration: EXIT_ANIMATION_MS / 1000,
                        ease: "easeIn",
                      }
                    : { type: "spring", stiffness: 320, damping: 28 }
                }
                onClick={() => {
                  if (!isRevealed && !exitDirection) handleReveal(topCard);
                }}
              >
                <motion.div
                  className="absolute inset-0"
                  style={{ transformStyle: "preserve-3d" }}
                  initial={false}
                  animate={{ rotateY: isRevealed ? 180 : 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 24,
                  }}
                >
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center p-8 sm:p-10 rounded-2xl"
                    style={{
                      backfaceVisibility: "hidden",
                      transform: "rotateY(0deg)",
                      background:
                        "linear-gradient(145deg, rgba(30,27,75,0.95) 0%, rgba(15,10,45,0.98) 100%)",
                    }}
                  >
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-3">
                      {t("swipeArena.termLabel", "Term")}
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-white text-center drop-shadow-lg">
                      {(topCard.front || "").trim() || "—"}
                    </p>
                  </div>
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center p-8 sm:p-10 rounded-2xl !bg-blue-500"
                    style={{
                      backfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-3">
                      {t("swipeArena.translationLabel", "Translation")}
                    </p>
                    <p className="text-xl sm:text-2xl font-semibold text-white/90 text-center">
                      {(topCard.back || "").trim() || "—"}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex gap-4 justify-center mt-8 px-2">
        <motion.button
          type="button"
          onClick={handleForgot}
          disabled={!topCard || !!exitDirection || !isRevealed}
          className="flex-1 max-w-[160px] py-4 px-6 rounded-2xl font-bold text-lg border-2 border-red-400/60 bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-400/50 disabled:opacity-50 disabled:pointer-events-none transition-colors flex flex-row items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label={t("swipeArena.forgot", "Forgot")}
        >
          <RotateCcw className="w-5 h-5 shrink-0" aria-hidden />
          <span className="whitespace-nowrap">{t("swipeArena.forgot", "Forgot")}</span>
        </motion.button>
        <motion.button
          type="button"
          onClick={handleKnow}
          disabled={!topCard || !!exitDirection || !isRevealed}
          className="flex-1 max-w-[160px] py-4 px-6 rounded-2xl font-bold text-lg border-2 border-emerald-400/60 bg-emerald-500/20 text-green-600 dark:text-green-400 hover:bg-emerald-500/30 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 disabled:opacity-50 disabled:pointer-events-none transition-colors flex flex-row items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label={t("swipeArena.know", "Know")}
        >
          <Check className="w-5 h-5 shrink-0" aria-hidden />
          <span className="whitespace-nowrap">{t("swipeArena.know", "Know")}</span>
        </motion.button>
      </div>
      <p className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-3 whitespace-nowrap">
        {t("swipeArena.shortcuts", "Space: flip | ← Forgot | Know →")}
      </p>
    </div>
  );
}
